import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Typography,
  CircularProgress,
  Paper,
  Divider,
  Box,
  useTheme,
} from "@mui/material";
import BookIcon from "@mui/icons-material/Book";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { useUserContext } from "../../context/UserContext";
import { useLessonContext } from "../../context/LessonContext";

const LessonsSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { state: userState } = useUserContext();
  const { 
    state: lessonState, 
    getBestUserLesson
  } = useLessonContext();
  const { userAttributes } = userState;
  const { 
    lessons, 
    userLessons, 
    isFetchingUserLessons, 
    hasLoadedUserLessons,
    isInitialUserLessonsLoad
  } = lessonState;

  // Extract the current lesson ID from the URL
  const getCurrentLessonId = () => {
    const pathParts = location.pathname.split('/');
    const lessonIndex = pathParts.findIndex(part => part === 'lesson');
    return lessonIndex !== -1 && pathParts[lessonIndex + 1] ? pathParts[lessonIndex + 1] : null;
  };

  const selectedLessonId = getCurrentLessonId();
  
  // Development QA toggle - shows best progress in subtitle (can be removed in production)
  const showProgressDebug = process.env.NODE_ENV === 'development';

  // Note: userLessons loading is now handled by LessonsLayout on mount
  // This component just renders from the cached context data



  const getLessonProgress = useCallback((lessonId) => {
    if (!userAttributes?.sub) return 0;
    
    const allMatches = userLessons.filter(ul => ul.userID === userAttributes.sub && ul.lessonID === lessonId);
    const bestUserLesson = getBestUserLesson(userLessons, userAttributes.sub, lessonId);
    const progress = bestUserLesson ? bestUserLesson.progress : 0;
    
    // Enhanced diagnostic logging with match count
    if (process.env.NODE_ENV === 'development') {
      console.log(`sidebar: lesson ${lessonId} best progress ${progress}% from ${allMatches.length} total records (completed=${progress===100})`);
      if (allMatches.length > 1) {
        console.log(`  - Multiple records found for ${lessonId}:`, allMatches.map(ul => `${ul.id}:${ul.progress}%`));
        console.log(`  - Selected best record: ${bestUserLesson?.id}:${bestUserLesson?.progress}%`);
      }
    }
    
    return progress;
  }, [userLessons, userAttributes?.sub, getBestUserLesson]);



  // Determine if a lesson should be accessible
  const isLessonAccessible = useCallback((lesson) => {
    const progress = getLessonProgress(lesson.id);
    
    // If lesson is completed, always accessible
    if (progress === 100) return true;
    
    // Check if this is the next available lesson
    const sortedLessons = [...lessons].sort((a, b) => a.order - b.order);
    const currentLessonIndex = sortedLessons.findIndex(l => l.id === lesson.id);
    
    // First lesson is always accessible
    if (currentLessonIndex === 0) return true;
    
    // Check if all previous lessons are completed
    for (let i = 0; i < currentLessonIndex; i++) {
      const prevLessonProgress = getLessonProgress(sortedLessons[i].id);
      if (prevLessonProgress < 100) return false;
    }
    
    return true;
  }, [lessons, getLessonProgress]);

  // Determine if a lesson is the next available lesson (for visual indication)
  const isNextAvailableLesson = useCallback((lesson) => {
    const progress = getLessonProgress(lesson.id);
    if (progress > 0) return false; // Already started or completed
    
    const sortedLessons = [...lessons].sort((a, b) => a.order - b.order);
    const currentLessonIndex = sortedLessons.findIndex(l => l.id === lesson.id);
    
    // First lesson is next if not started
    if (currentLessonIndex === 0) return true;
    
    // Check if all previous lessons are completed and this is the first non-started
    for (let i = 0; i < currentLessonIndex; i++) {
      const prevLessonProgress = getLessonProgress(sortedLessons[i].id);
      if (prevLessonProgress < 100) return false;
    }
    
    return true;
  }, [lessons, getLessonProgress]);

  const handleLessonSelect = (lesson) => {
    if (isLessonAccessible(lesson)) {
      navigate(`/lecciones/lesson/${lesson.id}`);
    }
  };

  const getLessonIcon = (progress, isNext = false) => {
    if (progress === 100) {
      return <CheckCircleIcon style={{ color: theme.palette.success.main }} />;
    } else if (progress > 0) {
      return <HourglassEmptyIcon style={{ color: theme.palette.warning.main }} />;
    } else if (isNext) {
      return <BookIcon style={{ color: theme.palette.primary.main }} />;
    } else {
      return <BookIcon />;
    }
  };

  const getLessonTextColor = (progress, isNext = false) => {
    if (progress === 100) {
      return theme.palette.success.main;
    } else if (progress > 0) {
      return theme.palette.warning.main;
    } else if (isNext) {
      return theme.palette.primary.main;
    } else {
      return 'inherit';
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height: '100%', 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Lecciones
        </Typography>
      </Box>
      
      {/* Show loading indicator when doing initial userLessons load */}
      {isInitialUserLessonsLoad && (isFetchingUserLessons || !hasLoadedUserLessons) ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: 3, 
          gap: 2 
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" align="center">
            Cargando lecciones...
          </Typography>
        </Box>
      ) : (
        <List sx={{ flexGrow: 1, overflow: 'auto', py: 0 }}>
          {lessons.map((lesson, index) => {
            const progress = getLessonProgress(lesson.id);
            const isAccessible = isLessonAccessible(lesson);
            const isNext = isNextAvailableLesson(lesson);
            const isSelected = selectedLessonId === lesson.id;
            
            return (
              <React.Fragment key={lesson.id}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleLessonSelect(lesson)}
                    disabled={!isAccessible}
                    selected={isSelected}
                    sx={{
                      opacity: isAccessible ? 1 : 0.5,
                      cursor: isAccessible ? 'pointer' : 'not-allowed',
                      backgroundColor: isSelected 
                        ? theme.palette.action.selected
                        : isNext 
                          ? 'rgba(33, 150, 243, 0.08)' 
                          : 'transparent',
                      '&:hover': {
                        backgroundColor: isAccessible 
                          ? isSelected
                            ? theme.palette.action.selected
                            : isNext 
                              ? 'rgba(33, 150, 243, 0.12)' 
                              : theme.palette.action.hover
                          : 'transparent'
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                        }
                      },
                      py: 1.5,
                      px: 2
                    }}
                  >
                    <ListItemIcon sx={{ opacity: isAccessible ? 1 : 0.5, minWidth: 40 }}>
                      {getLessonIcon(progress, isNext)}
                    </ListItemIcon>
                    <ListItemText
                      primary={lesson.title}
                      secondary={showProgressDebug ? `Mejor progreso: ${progress}%` : undefined}
                      primaryTypographyProps={{
                        style: { 
                          color: isAccessible 
                            ? isSelected
                              ? theme.palette.primary.main
                              : getLessonTextColor(progress, isNext)
                            : '#ccc',
                          fontWeight: isNext || isSelected ? 600 : 400,
                          fontSize: '0.95rem'
                        }
                      }}
                      secondaryTypographyProps={{
                        style: {
                          fontSize: '0.75rem',
                          color: progress === 100 ? theme.palette.success.main : theme.palette.text.secondary
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                {index < lessons.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default LessonsSidebar;